import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Server, Wifi, WifiOff, Trash2, AlertTriangle } from 'lucide-react'
import { devicesApi } from '../api/client'
import type { Device } from '../types'
import { formatDistanceToNow } from 'date-fns'
import clsx from 'clsx'

function DeviceStatusBadge({ status }: { status: Device['status'] }) {
  const config = {
    active: { color: 'bg-green-500/10 text-green-400 border-green-500/50', label: 'Active' },
    pending: { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/50', label: 'Pending' },
    revoked: { color: 'bg-red-500/10 text-red-400 border-red-500/50', label: 'Revoked' },
  }
  const { color, label } = config[status]

  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded border', color)}>
      {label}
    </span>
  )
}

export default function Devices() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(),
    refetchInterval: 10000,
  })

  const revokeMutation = useMutation({
    mutationFn: devicesApi.revoke,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: devicesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
  })

  const devices: Device[] = data?.devices ?? []

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 w-32 bg-slate-700 rounded mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-800 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Devices</h1>
        <Link to="/enroll" className="btn btn-primary">
          Enroll Device
        </Link>
      </div>

      {devices.length === 0 ? (
        <div className="card text-center py-12">
          <Server className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">No devices found</p>
          <p className="text-slate-500 text-sm mt-1">
            Install the agent on your first device to get started
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {devices.map((device) => (
            <div key={device.id} className="card hover:border-slate-600 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Server className="w-10 h-10 text-slate-400" />
                    {device.last_seen &&
                      new Date(device.last_seen).getTime() > Date.now() - 60000 ? (
                      <Wifi className="w-4 h-4 text-green-400 absolute -bottom-1 -right-1" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-slate-500 absolute -bottom-1 -right-1" />
                    )}
                  </div>
                  <div>
                    <Link
                      to={`/devices/${device.id}`}
                      className="text-white font-medium hover:text-primary-400 transition-colors"
                    >
                      {device.name || device.hostname || 'Unnamed Device'}
                    </Link>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                      <span>{device.hostname}</span>
                      {device.last_seen && (
                        <span>
                          Last seen{' '}
                          {formatDistanceToNow(new Date(device.last_seen), {
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <DeviceStatusBadge status={device.status} />

                  {device.status === 'active' && (
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to revoke this device?')) {
                          revokeMutation.mutate(device.id)
                        }
                      }}
                      className="p-2 text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors"
                      title="Revoke device"
                    >
                      <AlertTriangle className="w-5 h-5" />
                    </button>
                  )}

                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this device?')) {
                        deleteMutation.mutate(device.id)
                      }
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete device"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
